import fs from 'fs';
import path from 'path';
import { Config } from '@stencil/core';
import { reactOutputTarget } from '@stencil/react-output-target';
import { sass } from '@stencil/sass';
import { OutputTarget } from '@stencil/core/internal';

/**
 * This is a list of modules that are not transpiled by Babel.
 * We need to add them here so that Jest can transpile them.
 * See: https://stackoverflow.com/questions/68310447/how-to-tune-typescript-stenciljs-with-jest-to-support-es6-dynamic-module-imports
 */
const esmModules = ['d3-*', 'internmap', 'node-fetch', 'data-uri-to-buffer', 'fetch-blob', 'formdata-polyfill', 'fake-indexeddb'];

const isTest = !!process.env.IS_TEST;
const isCI = !!process.env.IS_CI || !!process.env.CI;

const dir = isTest ? 'dist-test' : 'dist';
const wwwDir = isTest ? 'www-test' : 'www';

const outputTargets: OutputTarget[] = [
  {
    type: 'dist',
    dir,
    esmLoaderPath: '../loader',
    isPrimaryPackageOutputTarget: true,
    transformAliasedImportPathsInCollection: true,
  },
  {
    type: 'dist-custom-elements',
    dir: `${dir}/components`,
    generateTypeDeclarations: true,
    externalRuntime: false,
    copy: [
      {
        src: '**/*.{jpg,png,svg}',
        dest: `${dir}/components/assets`,
        warn: true,
      },
    ],
  },
  {
    type: 'docs-readme',
  },
  {
    type: 'docs-json',
    file: 'components-docs.json',
    supplementalPublicTypes: 'src/types/index.ts',
  },
  {
    type: 'docs-vscode',
    file: 'vscode-data.json',
  },
  {
    type: 'www',
    dir: wwwDir,
    serviceWorker: null, // disable service workers
    copy: [{ src: 'pages' }, { src: '**/test/*.html' }, { src: '**/test/*.css' }],
    prerenderConfig: './prerender.config.ts',
  },
];

if (!isTest) {
  // Always generate React wrappers to the local @nylas/react package (for e2e testing)
  outputTargets.push(
    reactOutputTarget({
      outDir: '../nylas-react/lib/elements',
    }),
  );

  // Also generate to the external javascript repo if it exists (for publishing)
  const externalReactDir = path.resolve(__dirname, '../../../javascript/packages/react/src/elements');
  if (fs.existsSync(externalReactDir)) {
    outputTargets.push(
      reactOutputTarget({
        outDir: '../../../javascript/packages/react/src/elements',
      }),
    );
  }
}

export const config: Config = {
  namespace: 'nylas-web-elements',
  globalStyle: 'src/common/nylas-theme/index.css',
  taskQueue: 'async',
  sourceMap: true,
  devServer: {
    openBrowser: false,
  },
  transformAliasedImportPaths: true,
  extras: {
    enableImportInjection: true,
  },
  plugins: [
    sass({
      includePaths: ['./node_modules/'],
    }),
  ],
  rollupPlugins: {
    before: [],
    after: [],
  },
  outputTargets,
  validatePrimaryPackageOutputTarget: true,
  testing: {
    /**
     * Gitlab CI doesn't allow sandbox, therefor this parameters must be passed to your Headless Chrome
     * before it can run your tests
     */
    browserArgs: [
      '--no-sandbox',
      '--disable-setuid-sandbox',
      '--disable-dev-shm-usage',
      '--disable-gpu',
      '--window-size=1920,1080',
      ...(isCI ? ['--silent', '--disable-logging'] : []),
    ],
    browserHeadless: 'shell',
    transform: {
      '^.+\\.(ts|tsx|js|jsx|css)$': '@stencil/core/testing/jest-preprocessor',
    },
    transformIgnorePatterns: [`node_modules/(?!(?:.pnpm/)?(${esmModules.join('|')}))`],
    moduleNameMapper: {
      '^@/utils/(.*)$': '<rootDir>/src/utils/$1',
      '^@/version$': '<rootDir>/src/version',
    },
    globalSetup: '<rootDir>/jest.global-setup.ts',
    setupFilesAfterEnv: ['<rootDir>/jest.setup.ts'],
    // Suppress console logs in CI/CD environment
    ...(isCI && {
      silent: true,
      verbose: false,
    }),
  },
  globalScript: 'src/common/global.ts',
  buildEs5: false,
  minifyJs: true,
  minifyCss: true,
};
